import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ── Logo loader ───────────────────────────────────────────────────────────────
async function loadLogoDataUrl() {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = function () {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = '/lux_logo.PNG';
    } catch { resolve(null); }
  });
}

/**
 * Genera y descarga el PDF del Certificado de Estudios.
 *
 * @param {object} opts
 * @param {object}  opts.encabezado  - { nombre_completo, matricula, curp, programa, campus, institucion, codigo_programa }
 * @param {Array}   opts.ciclos      - [{ id_ciclo_escolar, nombre_ciclo, materias: [...] }]
 * @param {boolean} [opts.descargar=true]
 */
export async function generateCertificadoPDF({ encabezado, ciclos, descargar = true }) {
  const doc   = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const mL = 15;
  const mR = 15;
  const contentW = pageW - mL - mR;

  const logoDataUrl = await loadLogoDataUrl();

  // Espacio reservado para fotografía del estudiante en lado izquierdo de la tabla.
  const photoBoxW = 34;
  const photoGap = 4;

  // ── Mixed-text paragraph helper ───────────────────────────────────────────
  // Draws segments [{text, bold}] word-wrapped to maxWidth, returns new y.
  const drawMixedText = (segments, startX, startY, maxWidth, fontSize = 8, lineH = 4.5) => {
    doc.setFontSize(fontSize);

    // Tokenise into {text, bold, isSpace}
    const tokens = [];
    for (const seg of segments) {
      const parts = seg.text.split(/(\s+)/);
      for (const part of parts) {
        if (part) tokens.push({ text: part, bold: seg.bold, isSpace: /^\s+$/.test(part) });
      }
    }

    // Pack into lines
    const lines = [];
    let currentLine = [];
    let currentW = 0;

    for (const token of tokens) {
      doc.setFont('helvetica', token.bold ? 'bold' : 'normal');
      const w = doc.getTextWidth(token.text);

      if (token.isSpace) {
        if (currentLine.length > 0) { currentLine.push(token); currentW += w; }
      } else {
        if (currentW + w > maxWidth && currentLine.length > 0) {
          while (currentLine.length && currentLine[currentLine.length - 1].isSpace) {
            currentW -= doc.getTextWidth(currentLine[currentLine.length - 1].text);
            currentLine.pop();
          }
          lines.push(currentLine);
          currentLine = [];
          currentW = 0;
        }
        currentLine.push(token);
        currentW += w;
      }
    }
    if (currentLine.length) {
      while (currentLine.length && currentLine[currentLine.length - 1].isSpace) currentLine.pop();
      lines.push(currentLine);
    }

    // Draw
    let cy = startY;
    for (const lineTokens of lines) {
      let cx = startX;
      for (const t of lineTokens) {
        doc.setFont('helvetica', t.bold ? 'bold' : 'normal');
        doc.setFontSize(fontSize);
        doc.text(t.text, cx, cy);
        cx += doc.getTextWidth(t.text);
      }
      cy += lineH;
    }
    return cy;
  };

  // ── Data: flatten materias (deduplicated, best grade per materia) ──────────
  const ciclosOrdenados = [...ciclos].sort((a, b) => a.id_ciclo_escolar - b.id_ciclo_escolar);
  const materiaMap = new Map();

  for (const ciclo of ciclosOrdenados) {
    for (const m of (ciclo.materias || [])) {
      const key = m.id_materia != null ? m.id_materia : m.materia_nombre;
      const grade = m.final ?? m.extraordinario ?? m.parcial_3 ?? m.parcial_2 ?? m.parcial_1 ?? null;
      const existing = materiaMap.get(key);
      const gradeNum = grade !== null ? parseFloat(grade) : null;
      const existingNum = existing?.grade !== null && existing?.grade !== undefined ? parseFloat(existing.grade) : null;

      if (!existing || (gradeNum !== null && (existingNum === null || gradeNum > existingNum))) {
        materiaMap.set(key, {
          nombre: m.materia_nombre || '',
          creditos: m.creditos,
          cicloNombre: ciclo.nombre_ciclo,
          grade,
        });
      }
    }
  }

  const rows = Array.from(materiaMap.values())
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }));

  const fmtGrade = (val) => {
    if (val === null || val === undefined) return '';
    const n = parseFloat(val);
    return isNaN(n) ? '' : (Number.isInteger(n) ? String(n) : n.toFixed(1));
  };

  // ── Stats ─────────────────────────────────────────────────────────────────
  let totalCreditos = 0;
  let sumaCalif = 0;
  let countCalif = 0;
  for (const row of rows) {
    if (row.grade !== null && row.grade !== undefined) {
      const n = parseFloat(row.grade);
      if (!isNaN(n)) {
        sumaCalif += n;
        countCalif++;
        if (n >= 6) totalCreditos += parseFloat(row.creditos) || 0;
      }
    }
  }
  const promedioGeneral = countCalif > 0 ? (sumaCalif / countCalif).toFixed(1) : '—';
  const totalMaterias = rows.length;
  const materiasAprobadas = rows.filter((r) => r.grade !== null && parseFloat(r.grade) >= 6).length;

  // ─────────────────────────────────────────────────────────────────────────
  // PAGE 1
  // ─────────────────────────────────────────────────────────────────────────
  let y = 10;

  // ── Logo centrado grande ──────────────────────────────────────────────────
  const logoW = 46;
  const logoH = 38;
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', (pageW - logoW) / 2, y, logoW, logoH);
  }
  y += logoH + 5;

  // Sin título de institución (se deja solo el logo centrado)
  y += 1;

  // ── RVOE ──────────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(40, 40, 40);
  const rvoe1 = 'ESTUDIOS CON RECONOCIMIENTO DE VALIDEZ OFICIAL NÚMERO ABG-21050204 / 075 2005 DE FECHA 21 DE OCTUBRE DEL 2005 OTORGADO POR EL';
  const rvoe2 = 'GOBIERNO DEL ESTADO DE NUEVO LEÓN Y PUBLICADO EN EL PERIÓDICO OFICIAL DE FECHA 15 DE MARZO DEL 2006';
  doc.text(rvoe1, pageW / 2, y, { align: 'center' });
  y += 3.5;
  doc.text(rvoe2, pageW / 2, y, { align: 'center' });
  y += 7;

  // ── Título certificado ────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(0, 0, 0);
  doc.text('CERTIFICADO DE ESTUDIOS', pageW / 2, y, { align: 'center' });
  y += 8;

  // ── Párrafo certificatorio ────────────────────────────────────────────────
  const nombrePrograma = encabezado.programa || encabezado.codigo_programa || 'N/A';
  const modalidadRaw = (encabezado.modalidad || '').trim();
  const modalidadTexto = modalidadRaw
    ? modalidadRaw
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/(^|\s)([a-záéíóúñ])/g, (m, p1, p2) => p1 + p2.toUpperCase())
    : 'Mixta';
  const paragraphSegments = [
    { text: 'El Director a través del departamento de Control Escolar y Archivo ', bold: false },
    { text: 'CERTIFICA Y HACE CONSTAR QUE', bold: true },
    { text: ' según documentos que existen en el archivo general de esta institución la (el) C. ', bold: false },
    { text: encabezado.nombre_completo || '', bold: true },
    { text: ' con No de matrícula ', bold: false },
    { text: encabezado.matricula || '', bold: true },
    { text: ', CURP ', bold: false },
    { text: encabezado.curp || '', bold: true },
    { text: ' cursó y aprobó las siguientes asignaturas que cubren ', bold: false },
    { text: 'ÍNTEGRAMENTE', bold: true },
    { text: ' el plan de Estudios de ', bold: false },
    { text: nombrePrograma, bold: true },
    { text: ' Modalidad ', bold: false },
    { text: modalidadTexto, bold: true },
    { text: ' con las calificaciones que a continuación se expresan:', bold: false },
  ];

  doc.setTextColor(0, 0, 0);
  y = drawMixedText(paragraphSegments, mL, y, contentW, 8, 4.8);
  y += 5;

  // ── Tabla de asignaturas ──────────────────────────────────────────────────
  const tableBody = rows.map((row, i) => [
    String(i + 1),
    row.nombre,
    String(row.creditos ?? ''),
    row.cicloNombre,
    fmtGrade(row.grade),
  ]);

  const tableStartY = y;
  const tableLeft = mL + photoBoxW + photoGap;

  autoTable(doc, {
    startY: y,
    margin: { left: tableLeft, right: mR },
    head: [['N°', 'ASIGNATURAS', 'CR', 'PERIODO ESCOLAR', 'CALIFICACIÓN']],
    body: tableBody,
    theme: 'plain',
    styles: {
      fontSize: 7.5,
      cellPadding: { top: 1.2, bottom: 1.2, left: 2, right: 2 },
      textColor: [0, 0, 0],
      lineColor: [180, 180, 180],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 7.5,
      halign: 'center',
      lineColor: [0, 0, 0],
      lineWidth: 0.3,
    },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 'auto'               },
      2: { cellWidth: 10, halign: 'center' },
      3: { cellWidth: 32, halign: 'center' },
      4: { cellWidth: 25, halign: 'center' },
    },
    alternateRowStyles: { fillColor: [255, 255, 255] },
    didParseCell(data) {
      if (data.section === 'body' && data.column.index === 4) {
        const val = parseFloat(data.cell.raw);
        if (!isNaN(val) && val < 6 && val > 0) {
          data.cell.styles.textColor = [185, 28, 28];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
  });

  // Marco para fotografía — proporciones tipo credencial (aprox. 3:4).
  const photoBoxH = 38;
  doc.setDrawColor(140, 140, 140);
  doc.setLineWidth(0.25);
  doc.rect(mL, tableStartY, photoBoxW, photoBoxH);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(150, 150, 150);
  // Etiqueta centrada dentro del recuadro
  doc.text('FOTOGRAFÍA', mL + photoBoxW / 2, tableStartY + photoBoxH / 2 + 1.5, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  // Asegurar que y no quede por encima del borde inferior de la caja de foto.
  // Se incrementa la separación para que la firma no quede pegada a la fotografía.
  y = Math.max(doc.lastAutoTable.finalY, tableStartY + photoBoxH) + 20;

  // ── Bloque inferior ───────────────────────────────────────────────────────
  // Página extra si no hay espacio suficiente
  if (y + 40 > pageH - 10) {
    doc.addPage();
    y = 18;
  }

  // Línea de firma del alumno
  const firmaW = 52;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.line(mL, y, mL + firmaW, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(80, 80, 80);
  doc.text('FIRMA DEL ALUMNO', mL + firmaW / 2, y + 4, { align: 'center' });

  y += 12;

  // Notas y folio
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(0, 0, 0);
  doc.text('Nota: (*) Un asterisco señala asignaturas aprobadas en 2° oportunidad.', mL, y);
  y += 4;
  const folioLabel = 'N° de folio: ';
  const folioMatricula = String(encabezado.matricula || '').padStart(10, '0');
  doc.setFont('helvetica', 'normal');
  doc.text(folioLabel, mL, y);
  doc.setFont('helvetica', 'bold');
  doc.text(folioMatricula, mL + doc.getTextWidth(folioLabel), y);

  // ── Numeración de páginas ─────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(130, 130, 130);
    doc.text(`Página ${i} de ${totalPages}`, pageW - mR, pageH - 5, { align: 'right' });
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  const safeName = (encabezado.matricula || 'estudiante').replace(/[^a-zA-Z0-9_-]/g, '_');
  if (descargar) {
    doc.save(`Certificado_${safeName}.pdf`);
  } else {
    return doc.output('dataurlstring');
  }
}
